/**
 * Pre-launch preview access.
 *
 * With REACT_APP_COMING_SOON=true everyone sees the coming-soon page, except
 * browsers holding a preview pass. A pass is obtained by opening any URL with
 * `?preview=<code>`; the code is checked against the backend's PREVIEW_CODES
 * (POST /api/preview/verify), stored in localStorage, and the param is stripped
 * from the address bar. `?preview=off` drops the pass again.
 *
 * Stored passes are re-checked in the background on every visit, so removing a
 * code from PREVIEW_CODES revokes it on that person's next page load. Network
 * errors never revoke — only an explicit "invalid" from the server does.
 *
 * This is a UI gate only; the API is public either way.
 */

const STORAGE_KEY = 'sv_preview_code';
const COMING_SOON_BUILD = process.env.REACT_APP_COMING_SOON === 'true';
const VERIFY_TIMEOUT_MS = 5000;

const readPass = (): string | null => {
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
};

const writePass = (code: string | null) => {
	try {
		if (code) localStorage.setItem(STORAGE_KEY, code);
		else localStorage.removeItem(STORAGE_KEY);
	} catch {
		/* storage blocked — the pass just won't persist */
	}
};

/** True when this browser holds a preview pass (only meaningful in coming-soon builds). */
export const hasPreviewPass = (): boolean => COMING_SOON_BUILD && !!readPass();

/** Whether to show the coming-soon site. Call at render time, after resolvePreviewAccess(). */
export const isComingSoon = (): boolean => COMING_SOON_BUILD && !readPass();

/** true = valid, false = server said invalid, null = couldn't tell (network/5xx/rate limit). */
const verify = async (code: string): Promise<boolean | null> => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), VERIFY_TIMEOUT_MS);
	try {
		const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/preview/verify`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ code }),
			signal: controller.signal,
		});
		if (!res.ok) return null;
		const data = await res.json();
		return data.valid === true;
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
};

/**
 * Run once before the first render. Only waits on the network when the URL
 * carries a new `?preview=` code; returning visitors render immediately.
 */
export const resolvePreviewAccess = async (): Promise<void> => {
	if (!COMING_SOON_BUILD) return;

	const url = new URL(window.location.href);
	const fromUrl = url.searchParams.get('preview');

	if (fromUrl !== null) {
		url.searchParams.delete('preview');
		window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
		if (fromUrl === 'off') {
			writePass(null);
			return;
		}
		if ((await verify(fromUrl.trim())) === true) writePass(fromUrl.trim());
		return;
	}

	const stored = readPass();
	if (stored) {
		verify(stored).then((ok) => {
			if (ok === false) {
				writePass(null);
				window.location.reload();
			}
		});
	}
};
